import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { SelectChipStates, useTheme } from '@Sage/hoi-poi-ui';
import { BaseReactCell } from '@Sage/ssm-web5';
import { getLiteral } from 'utils/getLiteral';
import './styles.scss';

export const MARKETING_STATUS = {
    NO_CONSENT: 0,
    SUBSCRIBED: 1,
    CANCELED: 2,
};

const getStatusConfig = (theme) => ({
    [MARKETING_STATUS.NO_CONSENT]: {
        labelKey: 'label_without_consent',
        bgColor: theme.colors.background.utility025,
        hoverBgColor: theme.colors.actionMinor[150],
        borderColor: theme.colors.actionMinor[200],
        openBorderColor: theme.colors.actionMinor[600],
        textColor: theme.colors.actionMinor[600],
        iconColor: theme.colors.actionMinor[600],
        iconBgColor: theme.colors.background.utility025,
        iconType: 'withoutConsent',
    },
    [MARKETING_STATUS.SUBSCRIBED]: {
        labelKey: 'label_subscribed',
        bgColor: theme.fn.alpha(theme.colors.semantic.positiveCustom100, 0.5),
        hoverBgColor: theme.colors.semantic.positiveCustom100,
        borderColor: theme.colors.semantic.positiveCustom200,
        openBorderColor: theme.colors.semantic.positive200,
        textColor: theme.colors.semantic.positive500,
        iconColor: theme.colors.semantic.positive500,
        iconBgColor: theme.colors.semantic.positiveCustom100,
        iconType: 'personCheck',
    },
    [MARKETING_STATUS.CANCELED]: {
        labelKey: 'label_unsubscribed',
        bgColor: theme.fn.alpha(theme.colors.semantic.caution100, 0.5),
        hoverBgColor: theme.colors.semantic.caution100,
        borderColor: theme.colors.semantic.caution200,
        openBorderColor: theme.colors.semantic.caution200,
        textColor: theme.colors.semantic.caution600,
        iconColor: theme.colors.semantic.caution600,
        iconBgColor: theme.colors.semantic.caution100,
        iconType: 'personCancel',
    },
});

const normalizeStatus = (value) => {
    if (value === null || value === undefined || value === '') return MARKETING_STATUS.NO_CONSENT;

    const parsedValue = Number(value);
    if (Number.isNaN(parsedValue)) return MARKETING_STATUS.NO_CONSENT;

    if (Object.values(MARKETING_STATUS).includes(parsedValue)) return parsedValue;

    return MARKETING_STATUS.NO_CONSENT;
};

const MarketingStatusCell = memo(({ data, value, onChange, ...props }) => {
    const theme = useTheme();
    const [localStatus, setLocalStatus] = useState(normalizeStatus(value));

    useEffect(() => {
        setLocalStatus(normalizeStatus(value));
    }, [value]);

    const statusConfig = useMemo(() => getStatusConfig(theme), [theme]);

    const options = useMemo(
        () =>
            Object.entries(statusConfig).map(([statusValue, config]) => ({
                label: getLiteral(config.labelKey),
                value: Number(statusValue),
                bgColor: config.bgColor,
                hoverBgColor: config.hoverBgColor,
                borderColor: config.borderColor,
                openBorderColor: config.openBorderColor,
                textColor: config.textColor,
                iconColor: config.iconColor,
                iconBgColor: config.iconBgColor,
                iconType: config.iconType,
            })),
        [statusConfig],
    );

    const handleChange = useCallback(
        (opt) => {
            setLocalStatus(opt.value);
            onChange?.(data, opt);
        },
        [data, onChange],
    );

    return (
        <BaseReactCell data={data} {...props}>
            <div className="fm-marketing-status-cell">
                <SelectChipStates
                    options={options}
                    value={localStatus}
                    onChange={handleChange}
                    title={getLiteral('label_marketing_campaigns_change_status')}
                />
            </div>
        </BaseReactCell>
    );
});

MarketingStatusCell.displayName = 'MarketingStatusCell';

export default MarketingStatusCell;
